/**
 * LES SOURCES PENNYLANE DE L'ANALYSE — lot 96, 18/09/2026.
 *
 * Ce fichier ne dessine rien et n'appelle rien : il nomme les lectures, dit ce
 * qu'un droit manquant fait perdre, et ENCHAÎNE les morceaux de lecture que
 * l'API rend un par un. L'enchaînement est ici, et non dans le composant, pour
 * être éprouvé sans navigateur : une boucle qui ne s'arrête pas est la faute
 * la plus chère d'un écran de ce genre.
 */

export type RessourceSource =
  | 'comptes_bancaires'
  | 'transactions'
  | 'fournisseurs'
  | 'factures_fournisseurs'
  | 'factures_clients'
  | 'reglements_clients'
  | 'reglements_fournisseurs';

/** L'ordre de lecture : les factures avant leurs dates de paiement. */
export const ORDRE_SOURCES: readonly RessourceSource[] = [
  'comptes_bancaires',
  'transactions',
  'fournisseurs',
  'factures_fournisseurs',
  'factures_clients',
  'reglements_clients',
  'reglements_fournisseurs',
];

export const LIBELLES_SOURCES: Readonly<Record<RessourceSource, string>> = {
  comptes_bancaires: 'Comptes bancaires et soldes',
  transactions: 'Mouvements bancaires',
  fournisseurs: 'Fournisseurs',
  factures_fournisseurs: 'Factures fournisseurs',
  factures_clients: 'Factures clients',
  reglements_clients: 'Dates de paiement des clients',
  reglements_fournisseurs: 'Dates de paiement des fournisseurs',
};

export type IssueSource = 'complete' | 'partielle' | 'refusee' | 'injoignable';

/** Ce que l'API rend pour un morceau. */
export interface MorceauLu {
  readonly ressource: RessourceSource;
  readonly issue: IssueSource;
  readonly lignes: number;
  readonly ecartees: number;
  readonly suite: string | null;
  readonly restantes: number | null;
  readonly statut: number | null;
  readonly message: string | null;
  readonly champs_recus: readonly string[] | null;
}

/** L'état d'une source, tel que `GET /analyse/pennylane` le rend. */
export interface EtatSource {
  readonly ressource: RessourceSource;
  readonly lignes: number;
  readonly lu_le: string | null;
  readonly derniere: {
    readonly lance_le: string;
    readonly lance_par: string;
    readonly issue: IssueSource;
    readonly message: string | null;
  } | null;
  readonly restantes: number | null;
}

export type EtatDroit = 'ok' | 'manquant' | 'jeton_refuse' | 'injoignable' | 'erreur';

export interface DroitLu {
  readonly cle: string;
  readonly libelle: string;
  readonly perimetre: string;
  readonly sert_a: string;
  readonly etat: EtatDroit;
  readonly statut: number | null;
  readonly message: string | null;
}

export interface DroitsLus {
  readonly joignable: boolean;
  readonly message: string | null;
  readonly perimetres_annonces: readonly string[] | null;
  readonly droits: readonly DroitLu[];
}

export const LIBELLES_DROIT: Readonly<Record<EtatDroit, string>> = {
  ok: 'Accordé',
  manquant: 'Manquant',
  jeton_refuse: 'Jeton refusé',
  injoignable: 'Injoignable',
  erreur: 'Erreur',
};

/**
 * LA PHRASE DU BILAN DES DROITS. Un seul message, qui dit quoi faire : les
 * droits manquants sont à cocher chez Pennylane, un jeton refusé est à
 * reposer dans Cloudflare.
 */
export function bilanDesDroits(d: DroitsLus): string {
  if (!d.joignable && d.droits.every((x) => x.etat === 'injoignable')) {
    return d.message ?? "Pennylane n'a pas pu être joint.";
  }
  if (d.droits.some((x) => x.etat === 'jeton_refuse')) {
    return 'Pennylane refuse le jeton : il est absent, faux ou périmé. Il se pose dans les secrets du Worker (PENNYLANE_JETON).';
  }
  const manquants = d.droits.filter((x) => x.etat !== 'ok');
  if (manquants.length === 0) return 'Le jeton ouvre tout ce dont l’analyse a besoin.';
  return `À cocher chez Pennylane, en lecture seule : ${manquants
    .map((x) => x.perimetre)
    .join(', ')}.`;
}

/** Ce que l'enchaînement rapporte à l'écran à chaque morceau. */
export interface Avancement {
  readonly ressource: RessourceSource;
  readonly morceaux: number;
  readonly lignes: number;
  readonly ecartees: number;
  readonly restantes: number | null;
  readonly termine: boolean;
  readonly issue: IssueSource;
  readonly message: string | null;
}

/**
 * PLAFOND DE MORCEAUX PAR SOURCE. Mille lignes par morceau : deux cents
 * morceaux, c'est deux cent mille lignes — bien au-delà de seize mois de
 * TNS. Atteint, il signale une boucle, pas un gros volume, et on s'arrête en
 * le disant.
 */
export const MORCEAUX_MAX = 200;

/**
 * Lit une source jusqu'au bout, un morceau après l'autre.
 *
 * S'ARRÊTE : quand la suite est vide et qu'il ne reste rien ; au premier refus
 * ou coupure ; quand une recherche de règlements n'avance plus (le nombre de
 * pièces restantes ne baisse pas) ; au plafond.
 */
export async function lireJusquauBout(
  ressource: RessourceSource,
  lire: (ressource: RessourceSource, suite: string | null) => Promise<MorceauLu | null>,
  quandAvance: (a: Avancement) => void,
): Promise<Avancement> {
  let suite: string | null = null;
  let morceaux = 0;
  let lignes = 0;
  let ecartees = 0;
  let restantesAvant: number | null = null;

  for (;;) {
    const m = await lire(ressource, suite);
    morceaux += 1;
    if (m === null) {
      const fin: Avancement = {
        ressource,
        morceaux,
        lignes,
        ecartees,
        restantes: null,
        termine: true,
        issue: 'injoignable',
        message: 'Stafa ne répond pas.',
      };
      quandAvance(fin);
      return fin;
    }
    lignes += m.lignes;
    ecartees += m.ecartees;
    suite = m.suite;

    const bloque =
      m.restantes !== null && restantesAvant !== null && m.restantes >= restantesAvant;
    const plafond = morceaux >= MORCEAUX_MAX;
    const fini =
      m.issue === 'refusee' ||
      m.issue === 'injoignable' ||
      m.issue === 'complete' ||
      bloque ||
      plafond ||
      (suite === null && (m.restantes === null || m.restantes === 0));

    const message = bloque
      ? 'La recherche des dates de paiement n’avance plus : arrêtée.'
      : plafond && m.issue === 'partielle'
        ? `Arrêté après ${String(MORCEAUX_MAX)} morceaux : la lecture est incomplète.`
        : m.message;
    const a: Avancement = {
      ressource,
      morceaux,
      lignes,
      ecartees,
      restantes: m.restantes,
      termine: fini,
      issue: m.issue,
      message,
    };
    quandAvance(a);
    if (fini) return a;
    restantesAvant = m.restantes;
  }
}

/**
 * « il y a 3 h », « le 12/09 à 08:12 » — la fraîcheur d'une copie, en mots.
 * `maintenant` est passé pour être éprouvé à heure fixe.
 */
export function fraicheur(horodatage: string | null, maintenant: Date): string {
  if (horodatage === null) return 'jamais lue';
  const d = new Date(horodatage);
  if (Number.isNaN(d.getTime())) return 'date illisible';
  const minutes = Math.floor((maintenant.getTime() - d.getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${String(minutes)} min`;
  if (minutes < 24 * 60) return `il y a ${String(Math.floor(minutes / 60))} h`;
  const jour = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
  return `le ${jour.replace(' ', ' à ')}`;
}
